'use client'

import Spinner from '@/shared/components/Spinner'
import clsx from 'clsx'
import { useField, useFormikContext } from 'formik'
import type { HTMLInputTypeAttribute, ReactNode } from 'react'

type AuthTextFieldProps = {
  name: string
  label: string
  type?: HTMLInputTypeAttribute
  autoComplete: string
  placeholder?: string
  description?: string
  action?: ReactNode
  hasError?: boolean
  className?: string
}

export function AuthTextField({
  name,
  label,
  type = 'text',
  autoComplete,
  placeholder,
  description,
  action,
  hasError = false,
  className,
}: AuthTextFieldProps) {
  const [field, meta] = useField<string>(name)
  const { isSubmitting } = useFormikContext()
  const validationError = meta.touched ? meta.error : undefined
  const invalid = hasError || !!validationError

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <label
          htmlFor={name}
          className="text-[13.5px] font-semibold leading-[18px] text-[#0b0b0f]"
        >
          {label}
        </label>
        {action}
      </div>
      <input
        {...field}
        id={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={isSubmitting}
        aria-invalid={invalid}
        aria-describedby={
          validationError
            ? `${name}-error`
            : description
              ? `${name}-description`
              : undefined
        }
        className={clsx(
          'mt-2 block h-[50px] w-full rounded-[14px] border-[1.5px] bg-[#f4f4f6] px-4 py-0 text-[15px] leading-5 text-[#0b0b0f] shadow-none outline-none transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] placeholder:text-[#a0a0a8] focus:border-[#2563eb] focus:ring-0 disabled:cursor-not-allowed disabled:opacity-70 lg:mt-[7px] lg:h-[52px]',
          invalid ? 'border-[#c6453c]' : 'border-transparent',
        )}
      />
      {validationError ? (
        <p
          id={`${name}-error`}
          className="mt-2 text-[12.5px] font-medium leading-4 text-[#a2382e]"
        >
          {validationError}
        </p>
      ) : (
        description && (
          <p
            id={`${name}-description`}
            className="mt-2 text-[12.5px] leading-4 text-[#82828b] lg:mt-[7px]"
          >
            {description}
          </p>
        )
      )}
    </div>
  )
}

type AuthSubmitButtonProps = {
  children: ReactNode
  loading?: boolean
  disabled?: boolean
  className?: string
}

export function AuthSubmitButton({
  children,
  loading = false,
  disabled = false,
  className,
}: AuthSubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className={clsx(
        'flex h-[46px] w-full items-center justify-center rounded-full bg-[#0b0b0f] px-5 text-[14px] font-semibold leading-[18px] text-white transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#242429] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
    >
      {loading && (
        <span className="mr-2 flex">
          <Spinner size={4} />
        </span>
      )}
      {children}
    </button>
  )
}

type AuthErrorMessageProps = {
  children: ReactNode
  action?: ReactNode
  className?: string
}

export function AuthErrorMessage({
  children,
  action,
  className,
}: AuthErrorMessageProps) {
  return (
    <div
      role="alert"
      className={clsx(
        'flex flex-col gap-1 rounded-xl bg-[#fae9e7] px-3.5 py-2.5 text-[13px] leading-[17px] text-[#a2382e] sm:flex-row sm:items-center sm:gap-2.5',
        className,
      )}
    >
      <span className="font-medium">{children}</span>
      {action && (
        <span className="shrink-0 font-semibold sm:ml-auto">{action}</span>
      )}
    </div>
  )
}
